import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Clock, AlertCircle, GripVertical, TrendingUp } from 'lucide-react';
interface Contact {
  id: number;
  name: string;
  company: string;
  stage: string;
  dueDate: string;
  overdue: boolean;
}
const stages = ['Erster Kontakt', 'Problem/Bedarf', 'Interesse erfragen', 'Terminvorschlag', 'Termin gebucht', 'Follow Up', 'Wiedervorlage', 'Weitere Verwendung'];
export function SalesBoard() {
  const [view, setView] = useState<'board' | 'overdue' | 'week' | 'month'>('board');
  const [draggedContact, setDraggedContact] = useState<Contact | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([{
    id: 1,
    name: 'Thomas Weber',
    company: 'TechCorp',
    stage: 'Erster Kontakt',
    dueDate: '2025-01-28',
    overdue: false
  }, {
    id: 2,
    name: 'Max Mustermann',
    company: 'Innovation AG',
    stage: 'Interesse erfragen',
    dueDate: '2025-01-25',
    overdue: true
  }, {
    id: 3,
    name: 'Anna Schmidt',
    company: 'Digital Solutions',
    stage: 'Terminvorschlag',
    dueDate: '2025-01-29',
    overdue: false
  }, {
    id: 4,
    name: 'Michael Becker',
    company: 'StartUp Berlin',
    stage: 'Follow Up',
    dueDate: '2025-01-26',
    overdue: false
  }, {
    id: 5,
    name: 'Julia Fischer',
    company: 'Consulting Pro',
    stage: 'Wiedervorlage',
    dueDate: '2025-01-24',
    overdue: true
  }, {
    id: 6,
    name: 'Peter Müller',
    company: 'Tech GmbH',
    stage: 'Problem/Bedarf',
    dueDate: '2025-01-30',
    overdue: false
  }, {
    id: 7,
    name: 'Sarah Klein',
    company: 'Digital AG',
    stage: 'Termin gebucht',
    dueDate: '2025-01-27',
    overdue: false
  }]);
  const getContactsByStage = (stage: string) => {
    return contacts.filter(c => c.stage === stage);
  };
  const overdueContacts = contacts.filter(c => c.overdue);
  const handleContactClick = (contactId: number) => {
    console.log('Contact clicked:', contactId);
  };
  // Drag & Drop Handlers
  const handleDragStart = (e: React.DragEvent, contact: Contact) => {
    setDraggedContact(contact);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('opacity-50');
  };
  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedContact(null);
    setDragOverStage(null);
    e.currentTarget.classList.remove('opacity-50');
  };
  const handleDragOver = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };
  const handleDragLeave = () => {
    setDragOverStage(null);
  };
  const handleDrop = (e: React.DragEvent, targetStage: string) => {
    e.preventDefault();
    setDragOverStage(null);
    if (draggedContact && draggedContact.stage !== targetStage) {
      setContacts(contacts.map(contact => contact.id === draggedContact.id ? {
        ...contact,
        stage: targetStage
      } : contact));
      console.log(`Moved ${draggedContact.name} to ${targetStage}`);
    }
  };
  return <div className="p-6 space-y-6 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20 dark:bg-background min-h-screen" data-id="element-388">
      {/* Modern Header with Gradient */}
      <div className="flex items-center justify-between" data-id="element-389">
        <div className="space-y-1" data-id="element-390">
          <div className="flex items-center gap-3" data-id="element-391">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#eb6641] to-[#f97316] flex items-center justify-center shadow-sm" data-id="element-392">
              <TrendingUp className="h-5 w-5 text-white" data-id="element-393" />
            </div>
            <div data-id="element-394">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-[#eb6641] to-[#f97316] bg-clip-text text-transparent dark:text-foreground" data-id="element-395">
                Sales Board
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5" data-id="element-396">
                Vertriebspipeline und Follow-Up Übersicht • Drag & Drop zum
                Verschieben
              </p>
            </div>
          </div>
        </div>

        {/* Modern View Toggle Buttons */}
        <div className="flex gap-2 bg-white/80 dark:bg-card backdrop-blur-sm p-1.5 rounded-xl border border-orange-100 dark:border-border" data-id="element-397">
          <Button variant={view === 'board' ? 'default' : 'ghost'} size="sm" onClick={() => setView('board')} className={view === 'board' ? 'bg-gradient-to-r from-[#eb6641] to-[#f97316] shadow-sm' : 'hover:bg-orange-50 dark:hover:bg-white/5'} data-id="element-398">
            Board
          </Button>
          <Button variant={view === 'overdue' ? 'default' : 'ghost'} size="sm" onClick={() => setView('overdue')} className={view === 'overdue' ? 'bg-gradient-to-r from-[#eb6641] to-[#f97316] shadow-sm' : 'hover:bg-orange-50 dark:hover:bg-white/5'} data-id="element-399">
            Überfällig
            {overdueContacts.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-white/30 dark:bg-white/10" data-id="element-400">
                {overdueContacts.length}
              </span>}
          </Button>
          <Button variant={view === 'week' ? 'default' : 'ghost'} size="sm" onClick={() => setView('week')} className={view === 'week' ? 'bg-gradient-to-r from-[#eb6641] to-[#f97316] shadow-sm' : 'hover:bg-orange-50 dark:hover:bg-white/5'} data-id="element-401">
            Woche
          </Button>
          <Button variant={view === 'month' ? 'default' : 'ghost'} size="sm" onClick={() => setView('month')} className={view === 'month' ? 'bg-gradient-to-r from-[#eb6641] to-[#f97316] shadow-sm' : 'hover:bg-orange-50 dark:hover:bg-white/5'} data-id="element-402">
            Monat
          </Button>
        </div>
      </div>

      {/* Overdue View - Modern Card Design */}
      {view === 'overdue' && <Card className="border-0 shadow-md bg-[oklch(0.97_0_0)] dark:bg-card backdrop-blur-sm" data-id="element-403">
          <CardHeader className="border-b border-orange-100 dark:border-border bg-[#fef5ee] dark:bg-white/5" data-id="element-404">
            <CardTitle className="flex items-center gap-2 text-lg" data-id="element-405">
              <div className="h-8 w-8 rounded-lg bg-orange-500/10 dark:bg-[#eb6641]/20 flex items-center justify-center" data-id="element-406">
                <AlertCircle className="h-4 w-4 text-orange-600 dark:text-[#ff8a65]" data-id="element-407" />
              </div>
              <span className="text-foreground" data-id="element-408">Überfällige Kontakte</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6" data-id="element-409">
            <div className="space-y-3" data-id="element-410">
              {overdueContacts.map(contact => <div key={contact.id} onClick={() => handleContactClick(contact.id)} className="group flex items-center justify-between p-4 border border-orange-200 dark:border-border rounded-xl hover:shadow-md hover:border-orange-300 dark:hover:border-border hover:bg-orange-50/50 dark:hover:bg-white/5 transition-all cursor-pointer bg-[oklch(0.97_0_0)] dark:bg-card" data-id="element-411">
                  <div className="flex items-center gap-3" data-id="element-412">
                    <div className="h-10 w-10 rounded-full bg-[#eb6641]/10 dark:bg-[#eb6641]/20 flex items-center justify-center text-[#eb6641] dark:text-[#ff8a65] font-semibold shadow-sm" data-id="element-413">
                      {contact.name.charAt(0)}
                    </div>
                    <div data-id="element-414">
                      <p className="font-semibold text-foreground group-hover:text-[#eb6641] dark:group-hover:text-[#ff8a65] transition-colors" data-id="element-415">
                        {contact.name}
                      </p>
                      <p className="text-sm text-muted-foreground" data-id="element-416">
                        {contact.company} • {contact.stage}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 dark:bg-[#eb6641]/10 rounded-lg border border-orange-200 dark:border-[#eb6641]/20" data-id="element-417">
                    <Clock className="h-4 w-4 text-[#eb6641] dark:text-[#ff8a65]" data-id="element-418" />
                    <span className="text-sm font-medium text-[#eb6641] dark:text-[#ff8a65]" data-id="element-419">
                      {contact.dueDate}
                    </span>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      {/* Board View - Modern Kanban Design */}
      {view === 'board' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-420">
          {stages.map(stage => {
        const stageContacts = getContactsByStage(stage);
        const isDropTarget = dragOverStage === stage;
        return <div key={stage} onDragOver={e => handleDragOver(e, stage)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(e, stage)} className={`flex flex-col transition-all duration-300 ${isDropTarget ? 'scale-105' : ''}`} data-id="element-421">
                <Card className={`flex flex-col h-full border-0 shadow-md transition-all duration-300 ${isDropTarget ? 'bg-gradient-to-br from-orange-50 to-amber-50 dark:bg-white/10 ring-2 ring-[#eb6641] dark:ring-[#ff8a65] ring-offset-2 dark:ring-offset-background shadow-lg' : 'bg-[oklch(0.97_0_0)] dark:bg-card backdrop-blur-sm hover:shadow-lg'}`} data-id="element-422">
                  <CardHeader className="pb-3 border-b border-orange-100 dark:border-border bg-[#fef5ee] dark:bg-white/5" data-id="element-423">
                    <CardTitle className="text-sm font-semibold flex items-center justify-between" data-id="element-424">
                      <span className="text-foreground" data-id="element-425">{stage}</span>
                      <span className="text-xs font-bold bg-gradient-to-r from-[#eb6641] to-[#f97316] text-white px-2.5 py-1 rounded-full" data-id="element-426">
                        {stageContacts.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-3 p-4 min-h-[200px]" data-id="element-427">
                    {stageContacts.map(contact => <div key={contact.id} draggable onDragStart={e => handleDragStart(e, contact)} onDragEnd={handleDragEnd} onClick={() => handleContactClick(contact.id)} className={`group p-4 rounded-xl border-2 transition-all duration-200 cursor-move ${contact.overdue ? 'border-amber-300 dark:border-[#eb6641]/30 bg-gradient-to-br from-amber-50 to-orange-50 dark:bg-[#eb6641]/10 hover:shadow' : 'border-orange-100 dark:border-border bg-[oklch(0.97_0_0)] dark:bg-card hover:shadow-md hover:border-[#eb6641] dark:hover:border-[#ff8a65]'} hover:-translate-y-1`} data-id="element-428">
                        <div className="flex items-start gap-3" data-id="element-429">
                          <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" data-id="element-430" />
                          <div className="flex-1 min-w-0" data-id="element-431">
                            <div className="flex items-center gap-2 mb-2" data-id="element-432">
                              <div className="h-8 w-8 rounded-lg bg-[#eb6641]/10 dark:bg-[#eb6641]/20 flex items-center justify-center text-[#eb6641] dark:text-[#ff8a65] text-xs font-bold shadow-sm" data-id="element-433">
                                {contact.name.charAt(0)}
                              </div>
                              <p className="font-semibold text-sm text-foreground truncate" data-id="element-434">
                                {contact.name}
                              </p>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3 truncate" data-id="element-435">
                              {contact.company}
                            </p>
                            <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg ${contact.overdue ? 'bg-amber-100 dark:bg-[#eb6641]/20 text-amber-800 dark:text-[#ff8a65]' : 'bg-orange-50 dark:bg-[#eb6641]/10 text-orange-700 dark:text-[#ff8a65]'}`} data-id="element-436">
                              <Calendar className="h-3 w-3 flex-shrink-0" data-id="element-437" />
                              <span className="truncate font-medium" data-id="element-438">
                                {contact.dueDate}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>)}
                    {stageContacts.length === 0 && <div className="flex items-center justify-center h-32 border-2 border-dashed border-orange-200 dark:border-border rounded-xl bg-gradient-to-br from-orange-50/50 to-amber-50/30 dark:bg-white/5" data-id="element-439">
                        <p className="text-xs text-muted-foreground font-medium" data-id="element-440">
                          Ziehen Sie Kontakte hierher
                        </p>
                      </div>}
                  </CardContent>
                </Card>
              </div>;
      })}
        </div>}

      {/* Week/Month View - Modern List Design */}
      {(view === 'week' || view === 'month') && <Card className="border-0 shadow-md bg-[oklch(0.97_0_0)] dark:bg-card backdrop-blur-sm" data-id="element-441">
          <CardHeader className="border-b border-orange-100 dark:border-border bg-[#fef5ee] dark:bg-white/5" data-id="element-442">
            <CardTitle className="text-lg text-foreground" data-id="element-443">
              {view === 'week' ? 'Wochenübersicht' : 'Monatsübersicht'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6" data-id="element-444">
            <div className="space-y-3" data-id="element-445">
              {contacts.map(contact => <div key={contact.id} onClick={() => handleContactClick(contact.id)} className="group flex items-center justify-between p-4 border border-orange-100 dark:border-border rounded-xl hover:shadow-md hover:border-[#eb6641] dark:hover:border-[#ff8a65] hover:bg-orange-50/50 dark:hover:bg-white/5 transition-all cursor-pointer bg-[oklch(0.97_0_0)] dark:bg-card" data-id="element-446">
                  <div className="flex items-center gap-3 flex-1" data-id="element-447">
                    <div className="h-10 w-10 rounded-full bg-[#eb6641]/10 dark:bg-[#eb6641]/20 flex items-center justify-center text-[#eb6641] dark:text-[#ff8a65] font-semibold shadow-sm" data-id="element-448">
                      {contact.name.charAt(0)}
                    </div>
                    <div data-id="element-449">
                      <p className="font-semibold text-foreground group-hover:text-[#eb6641] dark:group-hover:text-[#ff8a65] transition-colors" data-id="element-450">
                        {contact.name}
                      </p>
                      <p className="text-sm text-muted-foreground" data-id="element-451">
                        {contact.company}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-452">
                    <span className="text-sm px-4 py-1.5 rounded-full bg-[#eb6641]/10 dark:bg-[#eb6641]/20 text-[#eb6641] dark:text-[#ff8a65] font-medium border border-orange-200 dark:border-[#eb6641]/30" data-id="element-453">
                      {contact.stage}
                    </span>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 dark:bg-[#eb6641]/10 rounded-lg border border-orange-200 dark:border-[#eb6641]/20" data-id="element-454">
                      <Calendar className="h-4 w-4 text-[#eb6641] dark:text-[#ff8a65]" data-id="element-455" />
                      <span className="text-sm font-medium text-foreground" data-id="element-456">
                        {contact.dueDate}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      {/* Modern Info Banner */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 dark:bg-white/5 border border-orange-200 dark:border-border" data-id="element-457">
        <div className="flex items-start gap-3" data-id="element-458">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#eb6641] to-[#f97316] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-459">
            <span className="text-white text-lg" data-id="element-460">💡</span>
          </div>
          <div data-id="element-461">
            <p className="text-sm font-medium text-foreground mb-1" data-id="element-462">
              Tipp für effizientes Arbeiten
            </p>
            <p className="text-sm text-muted-foreground" data-id="element-463">
              Ziehen Sie Kontakte zwischen den Spalten, um sie in der Pipeline
              zu verschieben. Überfällige Kontakte sind markiert.
            </p>
          </div>
        </div>
      </div>
    </div>;
}